import { fg } from '@atlaskit/platform-feature-flags';
import {
	type ConfluenceRangeFilter,
	type ThirdPartyFilters,
	type ThirdPartyProductFilter,
} from '@atlassian/search-client';

import { ThirdPartyConfigs } from '../../constants/3p-product-configs';
import { type FilterId } from '../../constants/filters';
import { NounSubtypeKey, TypeFilterValueKey } from '../../constants/filters/universal-type';
import { is3pProductKey, type ProductKeys, type ProductKeys3P } from '../../constants/products';
import {
	type ConnectorSource,
	FULL_CONNECTOR_SOURCE,
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../constants/schemas/3p-config';
import { ProductKey } from '../../constants/schemas/query-params';
import type { SelectedFiltersType } from '../../constants/types';
import { serveWeakLinkExpWithoutExposure } from '../experiments';

const GOOGLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/google';
const MICROSOFT_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';
const WEB_CRAWLER_INTEGRATION_ARI = 'ari:cloud:platform::integration/atlassian-web-crawler';
const GITHUB_INTEGRATION_ARI = 'ari:cloud:platform::integration/github';
const TEAMS_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';
const FIGMA_INTEGRATION_ARI = 'ari:cloud:platform::integration/figma';
const LOOM_INTEGRATION_ARI = 'ari:cloud:platform::integration/loom';
const DROPBOX_INTEGRATION_ARI = 'ari:cloud:platform::integration/dropbox';
const AIRTABLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/airtable';
const SMARTSHEET_INTEGRATION_ARI = 'ari:cloud:platform::integration/smartsheet';
const BITBUCKET_INTEGRATION_ARI = 'ari:cloud:platform::integration/bitbucket';
const NOTION_INTEGRATION_ARI = 'ari:cloud:platform::integration/notion';
const MIRO_INTEGRATION_ARI = 'ari:cloud:platform::integration/miro';
const ZENDESK_INTEGRATION_ARI = 'ari:cloud:platform::integration/zendesk';
const DOCUSIGN_INTEGRATION_ARI = 'ari:cloud:platform::integration/docusign';
const LUCID_INTEGRATION_ARI = 'ari:cloud:platform::integration/lucid';
const MONDAY_INTEGRATION_ARI = 'ari:cloud:platform::integration/monday';
const AZURE_DEVOPS_INTEGRATION_ARI = 'ari:cloud:platform::integration/azure-devops';
const GITLAB_INTEGRATION_ARI = 'ari:cloud:platform::integration/gitlab';
const CONFLUENCE_DC_INTEGRATION_ARI = 'ari:cloud:platform::integration/confluence-dc';
const CANVA_INTEGRATION_ARI = 'ari:cloud:platform::integration/canva';
const ASANA_INTEGRATION_ARI = 'ari:cloud:platform::integration/asana';
const BOX_INTEGRATION_ARI = 'ari:cloud:platform::integration/box';
const ZEPLIN_INTEGRATION_ARI = 'ari:cloud:platform::integration/zeplin';
const SERVICENOW_INTEGRATION_ARI = 'ari:cloud:platform::integration/servicenow';
const FRESHSERVICE_INTEGRATION_ARI = 'ari:cloud:platform::integration/freshservice';
const POWERBI_INTEGRATION_ARI = 'ari:cloud:platform::integration/microsoft';
const CLICKUP_INTEGRATION_ARI = 'ari:cloud:platform::integration/clickup';
const AMPLITUDE_INTEGRATION_ARI = 'ari:cloud:platform::integration/amplitude';
const STRIPE_INTEGRATION_ARI = 'ari:cloud:platform::integration/stripe';
const ADOBE_INTEGRATION_ARI = 'ari:cloud:platform::integration/adobe';
const AHA_INTEGRATION_ARI = 'ari:cloud:platform::integration/aha';
const MURAL_INTEGRATION_ARI = 'ari:cloud:platform::integration/mural';
const WEBEX_INTEGRATION_ARI = 'ari:cloud:platform::integration/cisco';
const TODOIST_INTEGRATION_ARI = 'ari:cloud:platform::integration/todoist';
const PAGERDUTY_INTEGRATION_ARI = 'ari:cloud:platform::integration/pagerduty';
const PIPEDRIVE_INTEGRATION_ARI = 'ari:cloud:platform::integration/pipedrive';
const HUBSPOT_INTEGRATION_ARI = 'ari:cloud:platform::integration/hubspot';
const DOVETAIL_INTEGRATION_ARI = 'ari:cloud:platform::integration/dovetail';
const LAUNCHDARKLY_INTEGRATION_ARI = 'ari:cloud:platform::integration/launchdarkly';
const SALESFORCE_INTEGRATION_ARI = 'ari:cloud:platform::integration/salesforce';
const SENTRY_INTEGRATION_ARI = 'ari:cloud:platform::integration/sentry';
const JIRA_DC_INTEGRATION_ARI = 'ari:cloud:platform::integration/jira-dc';
const GITBOOK_INTEGRATION_ARI = 'ari:cloud:platform::integration/graph-test-app';
const DATABRICKS_INTEGRATION_ARI = 'ari:cloud:platform::integration/databricks';

// Forge Integration ARIs - Dev versions
const FORGE_ADAM_VITAREQ_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/dAkghOClvWGokDftTTaIgNoD6pnNt74z';
const FORGE_ADAM_VITAREQ_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93';
const FORGE_K15T_SCROLL_SITES_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/h1n3NRFAn84v7ECSONVidlCS88qZh8cy';
const FORGE_K15T_SCROLL_SITES_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO';
const FORGE_PIO_AWS_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL';
const FORGE_PIO_AWS_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/mRcqWRwqkUd4JwapGkvGHUViyySX9MVy';
const FORGE_PIO_CLICKUP_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY';
const FORGE_PIO_CLICKUP_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K';
const FORGE_PIO_GITBOOK_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi';
const FORGE_PIO_GITBOOK_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u';
const FORGE_PIO_GITHUB_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j';
const FORGE_PIO_GITHUB_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI';
const FORGE_PIO_GITLAB_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/psr1wBkasRuKCwsvwi4aTJNhjmlVFebC';
const FORGE_PIO_GITLAB_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL';
const FORGE_PIO_GOOGLE_DRIVE_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq';
const FORGE_PIO_GOOGLE_DRIVE_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/pcIwW04B398CSnilRIyzPJ5v5GevlCCo';
const FORGE_PIO_GURU_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5';
const FORGE_PIO_GURU_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29';
const FORGE_PIO_HELPJUICE_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC';
const FORGE_PIO_HELPJUICE_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/8durapLyclxZeEexKkwxw6ZpCRpBz4dP';
const FORGE_PIO_HUBSPOT_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF';
const FORGE_PIO_HUBSPOT_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J';
const FORGE_PIO_SALESFORCE_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo';
const FORGE_PIO_SALESFORCE_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb';
const FORGE_PIO_SHAREPOINT_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/cl5i3J8L463hTuQiDt7f1mK4vZllsAo0';
const FORGE_PIO_SHAREPOINT_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/7yrzYPlAOcZAif2tIymeDXBUNUAxI95G';
const FORGE_PIO_STACK_OVERFLOW_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3';
const FORGE_PIO_STACK_OVERFLOW_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS';
const FORGE_UTOOLITY_GITBOOK_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5';
const FORGE_UTOOLITY_GITBOOK_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS';
const FORGE_GDRIVE_EUGENE_TEST_DEV_INTEGRATION_ARI =
	'ari:cloud:platform::integration/s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua';
const FORGE_GDRIVE_EUGENE_TEST_PROD_INTEGRATION_ARI =
	'ari:cloud:platform::integration/bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf';

// Default subtype mappings for entities when products don't have specific typeFilterConfig
const DEFAULT_ENTITY_SUBTYPES: Record<TypeFilterValueKey, NounSubtypeKey[]> = {
	[TypeFilterValueKey.Document]: [NounSubtypeKey.Document, NounSubtypeKey.Pdf],
	[TypeFilterValueKey.Spreadsheet]: [NounSubtypeKey.Spreadsheet],
	[TypeFilterValueKey.Presentation]: [NounSubtypeKey.Presentation],
	[TypeFilterValueKey.Video]: [NounSubtypeKey.Video],
	[TypeFilterValueKey.Design]: [NounSubtypeKey.Design],
	[TypeFilterValueKey.Conversation]: [],
	[TypeFilterValueKey.Message]: [],
	// Add other mappings as needed
} as any;

export const getEmptySelectedFilters = (): SelectedFiltersType => ({
	commonFilters: {},
	confluenceFilters: {
		spacesFilter: [],
		contributorsFilter: [],
		ancestorIdsFilter: [],
		range: [],
		owners: [],
		labelsFilter: [],
		titleMatchOnly: false,
		contentStatuses: [],
		containerStatus: [],
	},
	assetsFilters: {
		objectSchemaFilter: {
			schemaARIs: [],
		},
		objectTypeFilter: {
			typeARIs: [],
		},
	},
	jiraFilters: {
		issueFilter: {
			assigneeARIs: [],
			reporterARIs: [],
			commenterARIs: [],
			projectARIs: [],
			issueTypeIDs: [],
			issueLabels: [],
			issueTypes: [],
			statusCategories: [],
			watcherARIs: [],
		},
	},
	mercuryFilters: {
		owners: [],
		focusAreaTypeIds: [],
		ancestorIds: [],
	},
	talentFilters: {
		owners: [],
		employmentTypes: [],
		focusAreas: [],
		jobFamilies: [],
		statuses: [],
		teams: [],
	},
	compassFilters: {
		componentFilter: {
			componentStates: [],
		},
	},
	thirdPartyFilters: {
		titleMatchOnly: false,
		subtypes: [],
		integrations: [],
		createdBy: [],
		lastUpdatedBy: [],
		assignees: [],
		labels: [],
	},
});

export const convertCustomDateToRange = (from?: string, to?: string) => {
	const fromDate = from ? new Date(from) : new Date();
	const startOfTheFromDate = new Date(
		fromDate.getFullYear(),
		fromDate.getMonth(),
		fromDate.getDate(),
		0,
		0,
		0,
		0,
	);
	const toDate = to ? new Date(to) : new Date();
	const endOfTheToDate = new Date(
		toDate.getFullYear(),
		toDate.getMonth(),
		toDate.getDate(),
		23,
		59,
		59,
		999,
	);
	return {
		gt: from ? startOfTheFromDate.toISOString().split('Z')[0] : undefined,
		lt: to ? endOfTheToDate.toISOString().split('Z')[0] : undefined,
	};
};

// Copied from Confluence advanced-search package
// generates truncated iso strings for Confluence last modified filter defaults (today, yesterday, etc)
// considering the user's locale-based definition of start OR end of day
export const getUTCDateRange = (fromOffset: number, toOffset: number) => {
	const currentDate = new Date();
	return {
		gt: new Date(
			new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime() -
				fromOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
		lt: new Date(
			new Date(
				currentDate.getFullYear(),
				currentDate.getMonth(),
				currentDate.getDate(),
				23,
				59,
				59,
				999,
			).getTime() -
				toOffset * 24 * 60 * 60 * 1000,
		)
			.toISOString()
			.split('Z')[0],
	};
};

export const convertLastUpdatedToDateRange = (lastUpdated: string) => {
	let dateRangeValues;
	switch (lastUpdated) {
		case 'today':
			dateRangeValues = getUTCDateRange(0, 0);
			break;
		case 'yesterday':
			dateRangeValues = getUTCDateRange(1, 1);
			break;
		case 'past7Days':
			dateRangeValues = getUTCDateRange(7, 0);
			break;
		case 'past30Days':
			dateRangeValues = getUTCDateRange(30, 0);
			break;
		case 'pastYear':
			dateRangeValues = getUTCDateRange(365, 0);
			break;
		default:
			dateRangeValues = { gt: '', lt: '' };
	}
	return dateRangeValues;
};

export const getConfluenceDateOptionFromCustomDates = (
	from: string,
	to: string,
): ConfluenceRangeFilter => {
	const { gt, lt } = convertCustomDateToRange(from, to);
	return {
		field: 'LASTMODIFIED',
		gt,
		lt,
	};
};

export const getConfluenceDateOptionValues = (
	selectedDateOption: string,
): ConfluenceRangeFilter => {
	const dateRangeValues = convertLastUpdatedToDateRange(selectedDateOption);
	const { gt, lt } = dateRangeValues;
	return {
		field: 'LASTMODIFIED',
		gt,
		lt,
	};
};

export type IntegrationMap = Partial<Record<ProductKeys, string>>;

export const baseIntegrationARIs: IntegrationMap = {
	[ProductKey.GoogleDrive]: GOOGLE_INTEGRATION_ARI,
	[ProductKey.Sharepoint]: MICROSOFT_INTEGRATION_ARI,
	[ProductKey.AtlassianWebCrawler]: WEB_CRAWLER_INTEGRATION_ARI,
	[ProductKey.Teams]: TEAMS_INTEGRATION_ARI,
	[ProductKey.Figma]: FIGMA_INTEGRATION_ARI,
	[ProductKey.Gitbook]: GITBOOK_INTEGRATION_ARI,
	[ProductKey.Loom]: LOOM_INTEGRATION_ARI,
	[ProductKey.OneDrive]: MICROSOFT_INTEGRATION_ARI,
	[ProductKey.Github]: GITHUB_INTEGRATION_ARI,
	[ProductKey.Bitbucket]: BITBUCKET_INTEGRATION_ARI,
	[ProductKey.Airtable]: AIRTABLE_INTEGRATION_ARI,
	[ProductKey.Smartsheet]: SMARTSHEET_INTEGRATION_ARI,
	[ProductKey.Notion]: NOTION_INTEGRATION_ARI,
	[ProductKey.Miro]: MIRO_INTEGRATION_ARI,
	[ProductKey.Zendesk]: ZENDESK_INTEGRATION_ARI,
	[ProductKey.Docusign]: DOCUSIGN_INTEGRATION_ARI,
	[ProductKey.Lucid]: LUCID_INTEGRATION_ARI,
	[ProductKey.Monday]: MONDAY_INTEGRATION_ARI,
	[ProductKey.AzureDevops]: AZURE_DEVOPS_INTEGRATION_ARI,
	[ProductKey.Gitlab]: GITLAB_INTEGRATION_ARI,
	[ProductKey.ConfluenceDC]: CONFLUENCE_DC_INTEGRATION_ARI,
	[ProductKey.Canva]: CANVA_INTEGRATION_ARI,
	[ProductKey.OutlookCalendar]: MICROSOFT_INTEGRATION_ARI,
	[ProductKey.Asana]: ASANA_INTEGRATION_ARI,
	[ProductKey.Zeplin]: ZEPLIN_INTEGRATION_ARI,
	[ProductKey.ServiceNow]: SERVICENOW_INTEGRATION_ARI,
	[ProductKey.Freshservice]: FRESHSERVICE_INTEGRATION_ARI,
	[ProductKey.PowerBI]: POWERBI_INTEGRATION_ARI,
	[ProductKey.ClickUp]: CLICKUP_INTEGRATION_ARI,
	[ProductKey.Amplitude]: AMPLITUDE_INTEGRATION_ARI,
	[ProductKey.Stripe]: STRIPE_INTEGRATION_ARI,
	[ProductKey.AdobeXD]: ADOBE_INTEGRATION_ARI,
	[ProductKey.Aha]: AHA_INTEGRATION_ARI,
	[ProductKey.AdobeSign]: ADOBE_INTEGRATION_ARI,
	[ProductKey.Mural]: MURAL_INTEGRATION_ARI,
	[ProductKey.Webex]: WEBEX_INTEGRATION_ARI,
	[ProductKey.Todoist]: TODOIST_INTEGRATION_ARI,
	[ProductKey.PagerDuty]: PAGERDUTY_INTEGRATION_ARI,
	[ProductKey.Pipedrive]: PIPEDRIVE_INTEGRATION_ARI,
	[ProductKey.Hubspot]: HUBSPOT_INTEGRATION_ARI,
	[ProductKey.Dovetail]: DOVETAIL_INTEGRATION_ARI,
	[ProductKey.LaunchDarkly]: LAUNCHDARKLY_INTEGRATION_ARI,
	[ProductKey.Salesforce]: SALESFORCE_INTEGRATION_ARI,
	[ProductKey.Sentry]: SENTRY_INTEGRATION_ARI,
	[ProductKey.JiraDC]: JIRA_DC_INTEGRATION_ARI,
	[ProductKey.JsmDC]: JIRA_DC_INTEGRATION_ARI,
	[ProductKey.Box]: BOX_INTEGRATION_ARI,
	[ProductKey.Dropbox]: DROPBOX_INTEGRATION_ARI,
	[ProductKey.GoogleCalendar]: GOOGLE_INTEGRATION_ARI,
	[ProductKey.Gmail]: GOOGLE_INTEGRATION_ARI,
	[ProductKey.Databricks]: DATABRICKS_INTEGRATION_ARI,

	// Forge Products - Dev and Prod versions
	[ProductKey.ForgeAdamVitareqDev]: FORGE_ADAM_VITAREQ_DEV_INTEGRATION_ARI,
	[ProductKey.ForgeAdamVitareqProd]: FORGE_ADAM_VITAREQ_PROD_INTEGRATION_ARI,
	[ProductKey.ForgeK15tScrollSitesDev]: FORGE_K15T_SCROLL_SITES_DEV_INTEGRATION_ARI,
	[ProductKey.ForgeK15tScrollSitesProd]: FORGE_K15T_SCROLL_SITES_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioAwsDev]: FORGE_PIO_AWS_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioAwsProd]: FORGE_PIO_AWS_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioClickupDev]: FORGE_PIO_CLICKUP_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioClickupProd]: FORGE_PIO_CLICKUP_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioGitbookDev]: FORGE_PIO_GITBOOK_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioGitbookProd]: FORGE_PIO_GITBOOK_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioGithubDev]: FORGE_PIO_GITHUB_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioGithubProd]: FORGE_PIO_GITHUB_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioGitlabDev]: FORGE_PIO_GITLAB_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioGitlabProd]: FORGE_PIO_GITLAB_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioGoogleDriveDev]: FORGE_PIO_GOOGLE_DRIVE_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioGoogleDriveProd]: FORGE_PIO_GOOGLE_DRIVE_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioGuruDev]: FORGE_PIO_GURU_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioGuruProd]: FORGE_PIO_GURU_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioHelpjuiceDev]: FORGE_PIO_HELPJUICE_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioHelpjuiceProd]: FORGE_PIO_HELPJUICE_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioHubspotDev]: FORGE_PIO_HUBSPOT_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioHubspotProd]: FORGE_PIO_HUBSPOT_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioSalesforceDev]: FORGE_PIO_SALESFORCE_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioSalesforceProd]: FORGE_PIO_SALESFORCE_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioSharepointDev]: FORGE_PIO_SHAREPOINT_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioSharepointProd]: FORGE_PIO_SHAREPOINT_PROD_INTEGRATION_ARI,
	[ProductKey.ForgePioStackOverflowDev]: FORGE_PIO_STACK_OVERFLOW_DEV_INTEGRATION_ARI,
	[ProductKey.ForgePioStackOverflowProd]: FORGE_PIO_STACK_OVERFLOW_PROD_INTEGRATION_ARI,
	[ProductKey.ForgeUtoolityGitbookDev]: FORGE_UTOOLITY_GITBOOK_DEV_INTEGRATION_ARI,
	[ProductKey.ForgeUtoolityGitbookProd]: FORGE_UTOOLITY_GITBOOK_PROD_INTEGRATION_ARI,
	[ProductKey.ForgeGdriveEugeneTestDev]: FORGE_GDRIVE_EUGENE_TEST_DEV_INTEGRATION_ARI,
	[ProductKey.ForgeGdriveEugeneTestProd]: FORGE_GDRIVE_EUGENE_TEST_PROD_INTEGRATION_ARI,
};

export const getIntegrationARIsFromConfig = (): IntegrationMap => {
	const integrationMap: IntegrationMap = {};

	ThirdPartyConfigs.forEach((config, productKey3P) => {
		if (config.isEnabled()) {
			integrationMap[productKey3P] = config.integrationAri;
		}
	});

	return integrationMap;
};

export const getThirdPartyFilters = (
	products: ProductKeys[],
	titleMatchValue: boolean,
	// entities is the frontend filtered, already selected values
	entities: TypeFilterValueKey[],
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	range?: ConfluenceRangeFilter[],
	assignees?: string[],
	containerAris?: string[],
	createdBy?: string[],
	lastUpdatedBy?: string[],
	ancestorAris?: string[],
	labels?: string[],
	additionalTexts?: string[],
	selectedDatasource?: string,
): ThirdPartyFilters => {
	if (
		!products.length ||
		products.some(
			(p) => is3pProductKey(p) || isForgeConnectorProductKey(p) || p === ProductKey.Bitbucket,
		)
	) {
		// Conditionally create the dynamic integration ARIs based on the config
		const integrationARIsFromConfig = getIntegrationARIsFromConfig();

		// Combine static and dynamic integration ARIs
		let productToIntegrationARIMap: IntegrationMap = integrationARIsFromConfig;

		// Add Forge connector integration ARIs from thirdPartyConfigs
		products.forEach((product) => {
			if (isForgeConnectorProductKey(product) && thirdPartyConfigs[product]) {
				const integrationAri = thirdPartyConfigs[product]?.integrationAri;
				productToIntegrationARIMap[product] = integrationAri;
			}
		});

		// this is not covered under 3p static config
		productToIntegrationARIMap = {
			...productToIntegrationARIMap,
			[ProductKey.Bitbucket]: BITBUCKET_INTEGRATION_ARI,
		};

		// get the subtypes by looking at each type's subcategories
		let allSubtypes: NounSubtypeKey[] = [];
		let allContainerTypes: string[] = [];

		let thirdPartyProducts: ThirdPartyProductFilter[] = [];

		const filteredProducts = products
			.filter(
				(product) =>
					(is3pProductKey(product) || isForgeConnectorProductKey(product)) &&
					thirdPartyConfigs[product] !== undefined &&
					(!thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth ||
						isImpersonationEnabled(product, thirdPartyConfigs)) &&
					!thirdPartyConfigs[product as ProductKeys3P]?.isFederated,
			)
			.filter((product) => {
				return thirdPartyConfigs[product as ProductKeys3P]?.isEnabled?.();
			});

		for (let product of filteredProducts) {
			const thirdPartyConfig = thirdPartyConfigs[product as ProductKeys3P];
			if (!thirdPartyConfig) {
				continue;
			}

			let localSubtypes: NounSubtypeKey[] = [];
			let productEntities: string[] = [];
			let connectionIds: string[] | undefined;

			// Check if the product has specific typeFilterConfig for the requested entities
			let hasMatchingTypeConfig = false;
			thirdPartyConfig.typeFilterConfig?.forEach((entitiesConfig, entityValue) => {
				if (entities.includes(entityValue)) {
					localSubtypes = [...localSubtypes, ...entitiesConfig.subTypes3P];
					hasMatchingTypeConfig = true;
				}
			});

			if (fg('rovo-search-dynamic-forge-config')) {
				// Extract all entities from typeFilterConfig for this product
				if (thirdPartyConfig.typeFilterConfig) {
					thirdPartyConfig.typeFilterConfig.forEach((entitiesConfig) => {
						productEntities = [...productEntities, ...entitiesConfig.entities3P];
					});
					productEntities = [...new Set(productEntities)];
				}

				// Capture connectionId when present (dynamic Forge connectors)
				if (thirdPartyConfig.connectionId) {
					connectionIds = [thirdPartyConfig.connectionId];
				}
			}

			// If no specific typeFilterConfig matched, use default subtypes for the entities
			if (!hasMatchingTypeConfig && entities.length > 0) {
				entities.forEach((entity) => {
					if (DEFAULT_ENTITY_SUBTYPES[entity as TypeFilterValueKey]) {
						localSubtypes = [
							...localSubtypes,
							...DEFAULT_ENTITY_SUBTYPES[entity as TypeFilterValueKey],
						];
					}
				});
			}

			// Add local subtypes to the global subtypes array and remove duplicates
			allSubtypes = [...new Set([...allSubtypes, ...localSubtypes])];

			const integrationId = thirdPartyConfig.integrationAri;
			const providerId = thirdPartyConfig.providerId;
			const connectorSources = thirdPartyConfig.connectorSources;
			const datasourceId = thirdPartyConfig.datasourceId;
			product = product === ProductKey.JsmDC ? ProductKey.JiraDC : product;

			if (thirdPartyConfig.datasources && thirdPartyConfig.fanOutConnectorSources) {
				const datasourcesForQuery = selectedDatasource
					? thirdPartyConfig.datasources.filter((ds) => ds.datasourceId === selectedDatasource)
					: thirdPartyConfig.datasources;

				for (const datasourceConfig of datasourcesForQuery) {
					const datasourceContainerTypes = datasourceConfig.containerTypes || [];
					allContainerTypes = [...new Set([...allContainerTypes, ...datasourceContainerTypes])];

					const thirdPartyProductFilterForThisProduct = {
						integrationId: integrationId,
						subtypes: localSubtypes as string[],
						providerId,
						product: product as string,
						connectorSources,
						...(fg('add_datasourceid_to_search_graphql') && { datasourceId }),
						...(productEntities.length > 0 && { entities: productEntities }),
						...(connectionIds && { connectionIds }),
					};
					thirdPartyProducts.push({
						...thirdPartyProductFilterForThisProduct,
						...thirdPartyConfig.getSelectedDatasourceFilters?.(datasourceConfig),
					});
				}
			} else {
				thirdPartyProducts.push({
					integrationId: integrationId,
					subtypes: localSubtypes as string[],
					providerId,
					product: product as string,
					connectorSources,
					...(fg('add_datasourceid_to_search_graphql') && { datasourceId }),
					...(productEntities.length > 0 && { entities: productEntities }),
					...(connectionIds && { connectionIds }),
				});
			}
		}

		if (thirdPartyProducts) {
			// Add Loom and Bitbucket to the thirdPartyProducts filter
			const firstPartyProductsFor3pFilters: ProductKeys[] = [ProductKey.Loom, ProductKey.Bitbucket];

			const result = firstPartyProductsFor3pFilters
				.filter(
					(product) =>
						products.includes(product) && !thirdPartyProducts?.some((p) => p.product === product),
				)
				.map((product) => ({
					integrationId: productToIntegrationARIMap[product] || '',
					subtypes: allSubtypes as string[],
					product: product as string,
					connectorSources: [FULL_CONNECTOR_SOURCE] as ConnectorSource[],
				}));
			thirdPartyProducts = thirdPartyProducts.concat(result);
		}

		const createdByFilterField = { createdBy: createdBy || [] };

		const labelsFilterField = fg('enable_labels_filter_confluence_dc_search')
			? { labels: labels || [] }
			: {};

		const containerTypes =
			!fg('is_jsm_dc_enabled_in_ui') || thirdPartyProducts.length !== 1 ? [] : allContainerTypes;

		if (Object.keys(productToIntegrationARIMap).length !== 0) {
			const integrationARIs: string[] = products
				.filter(
					(product) =>
						(!is3pProductKey(product) && !isForgeConnectorProductKey(product)) ||
						!thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth ||
						isImpersonationEnabled(product, thirdPartyConfigs),
				)
				.map((product) => productToIntegrationARIMap[product])
				.filter((ari): ari is string => !!ari)
				// remove duplicates (eg sharepoint, teams)
				.filter((ari, pos, arr) => {
					return arr.indexOf(ari) === pos;
				});

			return {
				range,
				titleMatchOnly: titleMatchValue,
				subtypes: allSubtypes,
				integrations: integrationARIs,
				thirdPartyProducts,
				assignees,
				containerAris: containerAris || [],
				...createdByFilterField,
				ancestorAris: ancestorAris || [],
				...labelsFilterField,
				additionalTexts: additionalTexts || [],
				containerTypes: containerTypes,
			};
		} else {
			return {
				range,
				titleMatchOnly: titleMatchValue,
				subtypes: allSubtypes,
				thirdPartyProducts,
				assignees,
				containerAris: containerAris || [],
				...createdByFilterField,
				ancestorAris: ancestorAris || [],
				...labelsFilterField,
				additionalTexts: additionalTexts || [],
				containerTypes: containerTypes,
			};
		}
	}

	return {
		titleMatchOnly: false,
		subtypes: [],
		integrations: [],
	};
};

export enum DateRangeError {
	FROM_AFTER_TO_ERROR = 'fromAfterToError',
	FROM_IN_FUTURE_ERROR = 'fromInFutureError',
	TO_IN_FUTURE_ERROR = 'toInFutureError',
}

export const getInvalidDateRange = (from: string, to: string): DateRangeError | null => {
	if (!from && !to) {
		return null;
	}
	const dateFrom: Date = new Date(`${from}${from ? 'T00:00:00' : ''}`);
	const dateTo: Date = new Date(`${to}${to ? 'T00:00:00' : ''}`);
	const now: Date = new Date();

	if (dateFrom > now) {
		return DateRangeError.FROM_IN_FUTURE_ERROR;
	}

	if (dateFrom > dateTo) {
		return DateRangeError.FROM_AFTER_TO_ERROR;
	}

	if (dateTo > now) {
		return DateRangeError.TO_IN_FUTURE_ERROR;
	}

	return null;
};

export const getProducts3PFromConfigForFilter = (filterId: FilterId): ProductKeys3P[] => {
	const productsForFilter: ProductKeys3P[] = [];
	ThirdPartyConfigs.forEach((_, productKey3P) => {
		if (ThirdPartyConfigs.get(productKey3P)?.filters?.includes(filterId)) {
			productsForFilter.push(productKey3P);
		}
	});
	return productsForFilter;
};

export const notEmpty = <TValue>(value: TValue | null | undefined): value is TValue => {
	return value !== null && value !== undefined;
};

export const isImpersonationEnabled = (
	product: string,
	thirdPartyConfigs: Partial<Record<ProductKeys3P, ThirdPartyConfigsWithAPIResponse>>,
): boolean => {
	return !!(
		product === ProductKey.GoogleDrive &&
		thirdPartyConfigs[product as ProductKeys3P]?.impersonationEnabled &&
		serveWeakLinkExpWithoutExposure()
	);
};
